import { Component, OnInit, OnDestroy } from "@angular/core";
import { Location } from '@angular/common';
import { FormGroup, FormBuilder, Validators, AbstractControl, ValidatorFn } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { forkJoin, Observable, Subscription, BehaviorSubject,  } from 'rxjs';
import { mergeMap, tap } from "rxjs/operators";
import { AuthService } from './../../../services/auth.service';
import { ContactService } from "../../services/contacts.service";
import { DomainDefinitionService } from '../../../builder/services/domain-definition.service';
import { genId } from "../../../core/services/genId";
import { LoaderService } from '../../../common/services/loader.service';
import { OrganizationService } from '../../../common/services/organization.service';
import { SidePanelService } from "../../../common/services/side-panel.service";
import { SubHeaderService } from "../../../core/services/sub-header.service";
import { GroupService, GroupSearchSpec } from "../../services/group.service";
import { Group, GroupStub } from "../../models/group.model";
import { Address } from '../../../common/models/address.model';
import { AgentType, AgentTypeOptions, Contact } from "../../models/contact.model";
import { ContactType } from "../../models/contactType.model";
import { Department, Organization } from '../../../common/models/organization.model';
import { LunaDomain } from '../../../builder/models/domain.model';
import { Option } from "../../../common/models/flow.dto";
import { SidePanel } from "../../../common/models/side-panel.model";
import { SubHeader } from "../../../core/models/sub-header.model";
import { ValidatorPatterns } from '../../../common/classes/validator-patterns';
import { ContactHistoryEvent } from "../../models/contactHistory.model";
import { DynamicDialogService } from "../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../core/models/dialog.model";
import { LunaClaimTypes, LunaClaimValues } from "../../../models/user-identity.model";
import { ContactGroup } from "../../../admin/models/contactGroup.model";

@Component({
    selector: "contact-details",
    templateUrl: "./contact-details.component.html",
    styleUrls: ['./contact-details.component.scss'],
    standalone: false
})
export class ContactDetailsComponent implements OnInit, OnDestroy {

    private availableGroups: Group[] = [];

    private stopDisableEventPropagation: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    stopEventObservable: Observable<boolean> = this.stopDisableEventPropagation.asObservable();

    public _contact: Contact;
    public agentTypes = new Array<Option>();
    public contactDetailsForm: FormGroup;
    public contactOriginallyWasPrimaryContact: boolean = false;
    public contactOriginallyWasStatutoryAgent: boolean = false;
    public departments: Department[];
    public existingContact: boolean = false;
    public hideEmail: boolean = false;
    public inputMask = "+1 (###) ###-####";
    public isInternal: boolean = false;
    public organization: Organization;
    public isEntityOrOrganization: boolean = false;
    notesSidePanel: SidePanel;

    public contactHistorySidePanel: SidePanel = new SidePanel({
        tabs: [{ title: "Contact History" }],
        sections: [
            { componentName: 'ContactHistory', componentData: { data: null } }
        ],
        visible: false
    });
    public states: LunaDomain;
    public subHeader: SubHeader;
    public readOnlyMode: boolean = false;
    public hasAdminManageContactsPermissions: boolean = false;
    public hasEditContactsPermissions: boolean = false;
    public hasLimitedAdminManageContactsPermissions: boolean = false;
    public isDesignatedRepOrEmergencyPOCOrStatutoryAgent: boolean = false;
    groupMembershipChanged: boolean = false;
    subscriptions: Subscription = new Subscription();

    saveConfirmDialog: Dialog = new Dialog("PlaceHolder", [new DialogButton("Confirm", true, () => this.onSaveDialogConfirm()), new DialogButton("Cancel", false, () => this.onSaveDialogClose())]);
    historyNotLoadedDialog: Dialog = new Dialog("The Contact History hasn't finished loading yet.", [new DialogButton("OK", true, () => { })]);
    userUpdatedDialog: Dialog = new Dialog("Contact information has been successfully updated.", [new DialogButton("Exit", true, () => { this.goToManage(); })])
    lastActiveUserDialog: Dialog = new Dialog("<span class='padding-right'><svg-icon icon='icon-alert' class='red large'></svg-icon></span>Warning!  This is the last active contact for this account.  If disable is selected and confirmed, the organization will no longer be able to access this account throgh RICS.", [new DialogButton("CANCEL", false, () => { this.onUserDisableCancel(); }), new DialogButton("Confirm", true, () => { this.onUserDisableConfirm(); })]);
    userDeactivateDialog: Dialog = new Dialog("Deactivating this user will prevent this user from accessing the organization's information on RICS.  Do you wish to proceed?", [new DialogButton("No", false, () => { this.onUserDisableCancel(); }), new DialogButton("Yes", true, () => { this.onUserDisableConfirm(); })])

    private totalOrganizationActiveUsers: number = 0;


    get agentCellClass() : string {
        let cellClass = "cell small-12";
        if (this.agentTypes.length % 6 === 0) {
            cellClass += " large-2 medium-4";
        } else if (this.agentTypes.length % 4 === 0) {
            cellClass += " large-3 medium-6";
        } else {
            cellClass += " large-4 medium-6";
        }
        return cellClass;
    }

    get canCreateUser(): boolean {
        return (!this.existingContact || !this.contact.isUser) && (this.hasAdminManageContactsPermissions || (this.hasLimitedAdminManageContactsPermissions && this.isDesignatedRepOrEmergencyPOCOrStatutoryAgent));
    }

    get canDisableUser(): boolean {
        return this.authService.User.hasPermission(LunaClaimTypes.System, LunaClaimValues.LockUnlockContact);
    }

    get contact(): Contact | null {
        if (this.contactDetailsForm && this.contactDetailsForm.valid)
            this._contact = this._createContact();
        return this._contact;
    }

    set contact(c: Contact) {
        this._contact = c;        
    };

    get userGroups(): Group[] {
        if (this.contactDetailsForm == null) return [];
        const groupCtrl = this.contactDetailsForm.get("userGroupsSelect");
        if (groupCtrl == null) return [];
        return groupCtrl.value;
    }

    set userGroups(groups: Group[]) {
        if (this.contactDetailsForm == null) return;
        const groupCtrl = this.contactDetailsForm.get("userGroupsSelect");
        if (groupCtrl == null) return;
        groupCtrl.setValue(groups);
    }

    constructor(
        public readonly authService: AuthService,
        public readonly contactService: ContactService,
        public readonly organizationService: OrganizationService,
        public readonly activatedRoute: ActivatedRoute,
        public readonly formBuilder: FormBuilder,
        public subHeaderService: SubHeaderService,
        public location: Location,
        public loader: LoaderService,
        public domainService: DomainDefinitionService,
        public dialogService: DynamicDialogService,
        public sidePanelService: SidePanelService,
        public router: Router,
        private readonly groupService: GroupService
    ) {

    }

    ngOnInit(): void {
        const contactId = this.activatedRoute.snapshot.params["id"];
        const orgId = this.activatedRoute.snapshot.params["orgId"];
        this.isInternal = this.activatedRoute.snapshot.params["isInternal"] === 'true';
        this.subscriptions.add(this.domainService.getStates().subscribe(states => this.states = states));
        this.readOnlyMode = this.activatedRoute.snapshot.url.filter(a => a.path == 'view').length > 0;
        const buttons: any[] = [];
        if (contactId)
            this.existingContact = true;

        let obs$ = this.activatedRoute.data.pipe(
            mergeMap((data: any) => {
                if (data != null && data.contactGroup != null && data.contactGroup.contact != null) {
                    let contactGroup = <ContactGroup>data.contactGroup;
                    this.contact = contactGroup.contact;
                    if (contactGroup.enabledUsersInOrganization != null) {
                        this.totalOrganizationActiveUsers = contactGroup.enabledUsersInOrganization;
                    } else {
                        if (this.authService.User.id != this.contact.id && this.contact.canLogIn) {
                            this.totalOrganizationActiveUsers = 2;
                        } else {
                            this.totalOrganizationActiveUsers = 1;
                        }
                    }

                } else {
                    this.contact = new Contact();
                    this.contact.id = genId();
                    this.contact.internalContact = this.isInternal;
                    this.contact.organizationId = orgId;
                    this.contact.canLogIn = true;
                }
                this.loader.toggleLoader(true); //make sure the loader comes up since we have data to load for the page to become usable
                return this.groupService.get({ internal: this.authService.User.isInternal, stubs: false, fullContactDetails: false } as GroupSearchSpec);
            }),
            mergeMap((groups: Group | Group[] | GroupStub[]) => {
                this.availableGroups = groups as Group[];
                this.constructForm(this.contact);
                this.isDesignatedRepOrEmergencyPOCOrStatutoryAgent = (((this.contact.agentType & AgentType.DesignatedRep) == AgentType.DesignatedRep)
                    || ((this.contact.agentType & AgentType.EmergencyPOC) == AgentType.EmergencyPOC)
                    || ((this.contact.agentType & AgentType.StatutoryAgent) == AgentType.StatutoryAgent));

                let cancelOrReturnButtonName = "Return to Manage Contacts";
                if (!this.isReadOnly()) {
                    buttons.push({ name: "Save", method: () => this.save() });
                    cancelOrReturnButtonName = "Cancel";
                }
                buttons.push({ name: cancelOrReturnButtonName, method: () => this.cancel() });
                this.notesSidePanel = this.setupNotesSidePanel();
                if (this.authService.User.isInternal)
                    buttons.push({ name: "Notes", method: () => this.notes() });

                buttons.push({ name: "Contact History is Loading...", method: () => this.historyNotLoaded(), });

                //this.contact = r.contact;
                this.subHeader = new SubHeader({
                    title: this.contact.firstName + " " + this.contact.lastName, buttons: buttons
                });
                this.subHeaderService.updateSubHeader(this.subHeader);

                this.contactOriginallyWasPrimaryContact = this.isPrimaryContact(this.contact);
                this.contactOriginallyWasStatutoryAgent = this.isStatutoryAgent(this.contact);

                if (!this.isInternal) this.isInternal = this.contact.internalContact;
                if (!this._contact.address) this._contact.address = new Address();

                let orgObs: Observable<Organization>;

                if (this._contact && this._contact.organizationId != null && this._contact.organizationId != undefined) {
                    orgObs = this.organizationService.getOrganization(this._contact.organizationId);
                } else {
                    orgObs = this.organizationService.getOrganization(this.authService.User.organizationId);
                }
                orgObs = orgObs.pipe(tap((org: Organization) => {
                    this.organization = org;
                    this.departments = org.departments;
                    if (org.noPhone === true) this.setPhoneNotRequired();
                }));

                let contactHistory: Observable<ContactHistoryEvent[]>;
                contactHistory = this.contactService.getContactHistory(this.contact.id);

                return forkJoin([orgObs, contactHistory], (org: Organization, contactHistory: ContactHistoryEvent[]) => { return contactHistory; })

            }),
            tap((contactHistory) => {
                this.contactHistorySidePanel = new SidePanel({
                    tabs: [{ title: "Contact History" }],
                    sections: [
                        { componentName: 'ContactHistory', componentData: { data: contactHistory } }
                    ],
                    visible: false
                });

                //History loaded, remove the loading button
                buttons.splice(buttons.length - 1);
                //Add in the actual history button
                buttons.push({ name: "Contact History", method: () => this.history(), });
                this.subHeader = new SubHeader({
                    title: this.contact.firstName + " " + this.contact.lastName, buttons: buttons
                });
                this.subHeaderService.updateSubHeader(this.subHeader);
            }
            )
        );

        let dataSubs$ = obs$.pipe().subscribe(
                (data) => {
                   this.loader.toggleLoader(false);
                },
                (error) => {
                    console.error(error);
                    this.loader.toggleLoader(false);
                }
            );

        this.subscriptions.add(dataSubs$);

        let eventBlockSubs$ = this.stopEventObservable
            .subscribe(
                (blocked: boolean) => {
                    if (this.contactDetailsForm != null) {
                        let disableCtrl = this.contactDetailsForm.get("disableAccount");
                        if (disableCtrl != null) {
                            if (blocked) {
                                disableCtrl.disable(); //temporarily disable the control to keep it from being spammed
                            } else {
                                disableCtrl.enable();
                            }
                        }
                    }
                },
                (error) => { console.error(error); }
        );
        this.subscriptions.add(eventBlockSubs$);

        this.hasEditContactsPermissions = this.authService.User.allClaims.filter(a => a.type == LunaClaimTypes.System && a.value == LunaClaimValues.EditContact).length > 0
            || (this.authService.User.id === contactId && this.authService.User.allClaims.filter(a => a.type == LunaClaimTypes.System && a.value == LunaClaimValues.ManageOwnContact).length > 0);
        this.hasAdminManageContactsPermissions = this.authService.User.allClaims.filter(a => a.type == LunaClaimTypes.System && a.value == LunaClaimValues.ManageContacts).length > 0;
        this.hasLimitedAdminManageContactsPermissions = this.authService.User.allClaims.filter(a => a.type == LunaClaimTypes.System && a.value == LunaClaimValues.ManageContactsLimited).length > 0;
    }

    ngOnDestroy() {
        if (this.subscriptions) this.subscriptions.unsubscribe();
    }

    public agentTypesUpdated(role: string, isChecked: boolean): void {
        const roleNumber: number = parseInt(role);
        let control = this.contactDetailsForm.controls.agentTypes;

        if (!this.isReadOnly()) {
            if (control.value.filter(x => x === roleNumber).length > 0) {
                let arr = control.value;
                arr.splice(arr.indexOf(roleNumber), 1)
                control.setValue(arr);
            } else {
                let arr = control.value;
                arr.push(roleNumber);
                control.setValue(arr);
            }
        }
    }

    public cancel(): void {
        this.location.back();
    }

    public notes(): void {
        this.sidePanelService.newSidePanel(this.notesSidePanel);
        this.sidePanelService.toggleSidePanel(true);
    }

    public constructForm(contact: Contact): void {
        this.loadAgentTypes();
        if (!contact.address)
            contact.address = new Address();

        let keys = Object.keys(AgentType);
        let filteredKeys = keys.filter(k => k.match(/^\d+$/));
        let agentTypeUnfilteredValues = filteredKeys.map(k => parseInt(k) as number);
        let agentTypeValues = agentTypeUnfilteredValues.filter(a => (contact.agentType & a) > 0);
        this.contactDetailsForm = this.formBuilder.group({
            isInternal: [contact.internalContact],
            isUser: [{ value: contact.isUser, disabled: this.isReadOnly() }],
            agentTypes: [{ value: agentTypeValues, disabled: this.isReadOnly() }],
            disableAccount: [{ value: !contact.canLogIn, disabled: this.isReadOnly() || !this.canDisableUser }],
            isEntityOrOrganization: [{ value: contact.isEntityOrOrganization, disabled: this.isReadOnly() }],
            entityOrOrganizationName: [{ value: contact.entityOrOrganizationName, disabled: this.isReadOnly() }, [this.entityOrOrganizationNameFieldValidator()]],
            departmentSelect: [{ value: contact.departmentId, disabled: this.isReadOnly() }],
            middleInitial: [{ value: contact.middleInitial, disabled: this.isReadOnly() }],
            firstName: [{ value: contact.firstName, disabled: this.isReadOnly() }, [this.firstNameFieldValidator()]],
            lastName: [{ value: contact.lastName, disabled: this.isReadOnly() }, [this.lastNameFieldValidator()]],
            suffix: [{ value: contact.suffix, disabled: this.isReadOnly() }],
            fullLegalName: [{ value: contact.fullLegalName, disabled: this.isReadOnly() }],
            addressId: [contact.address.id],
            address: this.contactIsOnlyEmergency()
                ? [{ value: contact.address.street, disabled: this.isReadOnly() }]
                : [{ value: contact.address.street, disabled: this.isReadOnly() }, [Validators.required]],
            suite: [{ value: contact.address.suite, disabled: this.isReadOnly() }],
            contactId: [contact.id],
            poBox: [{ value: contact.address.poBox, disabled: this.isReadOnly() }],
            city: this.contactIsOnlyEmergency()
                ? [{ value: contact.address.city, disabled: this.isReadOnly() }]
                : [{ value: contact.address.city, disabled: this.isReadOnly() }, [Validators.required]],
            addressStateCode: this.contactIsOnlyEmergency()
                ? [{ value: contact.address.stateCode, disabled: this.isReadOnly() }]
                : [{ value: contact.address.stateCode, disabled: this.isReadOnly() }, [Validators.required]],
            zipCode: this.contactIsOnlyEmergency()
                ? [{ value: contact.address.zipCode, disabled: this.isReadOnly() }]
                : [{ value: contact.address.zipCode, disabled: this.isReadOnly() }, [Validators.required, Validators.pattern(ValidatorPatterns.ZipCode)]],
            emailAddress: [{ value: contact.emailAddress, disabled: this.isReadOnly() }, [Validators.required, Validators.pattern(ValidatorPatterns.Email)]],
            primaryPhone: [{ value: contact.primaryPhone, disabled: this.isReadOnly() }, [Validators.required, Validators.pattern(ValidatorPatterns.Phone)]],
            secondaryPhone: [{ value: contact.secondaryPhone, disabled: this.isReadOnly() }, [Validators.pattern(ValidatorPatterns.Phone)]],
            fax: [{ value: contact.fax, disabled: this.isReadOnly() }, [Validators.pattern(ValidatorPatterns.Phone)]],
            userGroupsSelect: [{ value: this.availableGroups.filter(g => g.users.findIndex(u => contact.id === u.id) !== -1), disabled: this.isReadOnly() }],
            certifiedMailLogin: [{ value: contact.certifiedMailLogin, disabled: this.isReadOnly() }],
            certifiedMailId: [{ value: contact.certifiedMailId, disabled: this.isReadOnly() }]
        });
    }

    public formValue(controlName: string): any {
        return this.contactDetailsForm.controls[controlName].value;
    }

    goToManage(): void {
        const destination = (this.contact.organizationId == this.authService.User.organizationId)
            ? "/admin/manage-contacts"
            : `/common/organization/${this.contact.organizationId}`
        this.router.navigate([destination]);
    }

    public isPrimaryContact(contact: Contact): boolean {
        return contact.agentType ? (contact.agentType & AgentType.PrimaryContact) == AgentType.PrimaryContact : false;
    }

    public isReadOnly(): boolean {
        return (this.authService.User.isInternal != this.contact.internalContact || this.readOnlyMode) || !this.hasEditContactsPermissions;
    }

    public isStatutoryAgent(contact: Contact): boolean {
        return (contact.agentType & AgentType.StatutoryAgent) == AgentType.StatutoryAgent;
    }

    public loadAgentTypes(): void {
        this.agentTypes = AgentTypeOptions.All;
    }

    onUserDisable(): void {
        if (this.stopDisableEventPropagation.value == false) {
            let isDisable = this.contactDetailsForm.get('disableAccount').value;
            if (isDisable) {
                let dialog: Dialog;
                if (this.totalOrganizationActiveUsers < 2) {
                    dialog = this.lastActiveUserDialog;
                } else {
                    dialog = this.userDeactivateDialog;
                }
                dialog.toggleDialog(true);
                this.dialogService.updateDialog(dialog);
            } else {
                this.saveContact();
            }
        }
    }

    onUserDisableCancel(): void {
        this.stopDisableEventPropagation.next(true);
        this.contactDetailsForm.get('disableAccount').setValue(false);
        setTimeout(() => { this.stopDisableEventPropagation.next(false); }, 1000);
    }

    onUserDisableConfirm(): void {
        this.saveContact();
    }

    public save(): void {
        if (this.contactDetailsForm.valid) {
            let contact = this.contact;
            let contactIsPrimary = this.isPrimaryContact(contact);
            let contactIsStatutory = this.isStatutoryAgent(contact);
            let primaryContactChanged = false;
            let statutoryAgentChanged = false;

            if (contactIsPrimary && !this.contactOriginallyWasPrimaryContact) {
                primaryContactChanged = true;
            }

            if (contactIsStatutory && !this.contactOriginallyWasStatutoryAgent) {
                statutoryAgentChanged = true;
            }

            if (primaryContactChanged || statutoryAgentChanged) {
                this.loader.toggleLoader(true);
                this.contactService.getContactsByOrganization(contact.organizationId).toPromise().then(orgContacts => {
                    let dialogMessage = '';
                    let otherPrimaryContacts = orgContacts.filter(a => this.isPrimaryContact(a) && (a.id != this.contact.id));
                    let otherStatutoryAgents = orgContacts.filter(a => this.isStatutoryAgent(a) && (a.id != this.contact.id));

                    if (primaryContactChanged && otherPrimaryContacts && otherPrimaryContacts.length > 0) {
                        var primaryContactNames = otherPrimaryContacts.map(a => a.displayName).join(", ");
                        dialogMessage += `You are about to replace the existing Primary Contact with a new Primary Contact. The following user(s) will lose this agent type:`;
                        dialogMessage += ` ${primaryContactNames}. If you wish to proceed, click Confirm. Otherwise, click Cancel.`;
                    }

                    if (statutoryAgentChanged && otherStatutoryAgents && otherStatutoryAgents.length > 0) {
                        var statutoryAgentNames = otherStatutoryAgents.map(a => a.displayName).join(", ");
                        if (dialogMessage.length > 0) dialogMessage += ` -AND- `;
                        dialogMessage += `You are about to replace the existing Statutory Agent with a new Statutory Agent. The following user(s) will lose this agent type:`;
                        dialogMessage += ` ${statutoryAgentNames}. If you wish to proceed, click Confirm. Otherwise, click Cancel.`;
                    }
                    this.loader.toggleLoader(false);
                    if (dialogMessage.length > 0) {
                        this.saveConfirmDialog.message = dialogMessage;
                        this.saveConfirmDialog.toggleDialog(true);
                        this.dialogService.updateDialog(this.saveConfirmDialog);                        
                    } else {
                        this.saveContact();
                    }
                });
            } else {
                this.saveContact();
            }
        } else { // invalid form
            // Touch all the controls to reveal validation errors
            Object.keys(this.contactDetailsForm.controls).forEach(field => {
                const control = this.contactDetailsForm.get(field);
                control.markAsTouched({ onlySelf: true });
            });
        }
    }

    public saveContact(): void {
        this.stopDisableEventPropagation.next(true);
        let contact = this.contact!;

        // canLogIn must reflect the disableAccount checkbox even when the rest of the form
        // is invalid (e.g. legacy contact data failing newer field validators), since the
        // "contact" getter silently skips rebuilding from the form in that case.
        const disableAccountValue = this.contactDetailsForm.get('disableAccount')!.value;
        contact.canLogIn = contact.isUser && !disableAccountValue;
        let obs$ = this.contactService.save(contact);
        if (this.groupMembershipChanged) {
            this.userGroups.forEach(u => {
                const group = this.availableGroups.find(g => g.id === u.id);
                if (group != null) {
                    if (group.users.find(u => u.id === contact.id) == null) {
                        group.users.push(contact);
                        obs$ = obs$.pipe(mergeMap((d) => {
                            return this.groupService.save(group);
                        }));
                    }                    
                }
            });
            if (this.existingContact === true) { //only check to modify any other groups if the user isn't new; don't need to update groups the user doesn't belong to for a new user
                this.availableGroups.filter(g => this.userGroups.find(u => u.id === g.id) == null).forEach(g => {
                    let userIndex = g.users.findIndex(u => u.id === contact.id);
                    if (userIndex !== -1) { //only bother resaving the group with new membership if the user had previously been a member of the group and isn't any longer
                        g.users.splice(userIndex, 1);
                        obs$ = obs$.pipe(mergeMap((d) => {
                            return this.groupService.save(g);
                        }));
                    }
                })
            }
        }
        this.loader.toggleLoader(true);
        this.subscriptions.add(obs$.subscribe(c => {
            this.loader.toggleLoader(false);
            this.stopDisableEventPropagation.next(false);
            this.userUpdatedDialog.toggleDialog(true);
            this.dialogService.updateDialog(this.userUpdatedDialog);
        },
        (error) => {
            console.error(error);
            this.dialogService.showError();
            this.loader.toggleLoader(false);
        }));
    }

    setPhoneNotRequired(): void {
        this.contactDetailsForm.get('primaryPhone').clearValidators();
        this.contactDetailsForm.get('primaryPhone').setValidators([Validators.pattern(ValidatorPatterns.Phone)]);
        this.contactDetailsForm.get('primaryPhone').updateValueAndValidity();
    }

    private setupNotesSidePanel(): SidePanel {
        return new SidePanel({
            tabs: [{ title: "Contact Notes" }],
            sections: [
                { componentName: "ContactNotes", componentData: { data: this.contact } }
            ],
            visible: false
        });
    }

    public updateContactType(type: string): void {
        this._contact.internalContact = type.toLowerCase() === ContactType.internal;
    }

    public userHasRole(role: string): boolean {
        const roleNumber: number = parseInt(role);
        let contact = this.contact;
        let value = (contact.agentType & roleNumber) !== 0;
        return value;
    }

    public _createContact(): Contact {
        if (!this.contactDetailsForm || !this.contactDetailsForm.valid)
            return this._contact;

        let contact = new Contact();
        let controls = this.contactDetailsForm.controls;
        contact.address.id = controls.addressId.value;
        contact.address.city = controls.city.value;
        contact.address.poBox = controls.poBox.value;
        contact.address.stateCode = controls.addressStateCode.value;
        contact.address.street = controls.address.value;
        contact.address.suite = controls.suite.value;
        contact.address.zipCode = controls.zipCode.value;
        contact.agentType = controls.agentTypes.value.reduce((a, c) => a | c, AgentType.None);
        contact.canLogIn = !controls.disableAccount.value;
        contact.certifiedMailId = controls.certifiedMailId.value;
        contact.certifiedMailLogin = controls.certifiedMailLogin.value;
        contact.configTypeName = this._contact.configTypeName;
        contact.departmentId = controls.departmentSelect.value;
        contact.emailAddress = controls.emailAddress.value;
        contact.fax = controls.fax.value;
        contact.firstName = controls.firstName.value;
        contact.fullLegalName = controls.fullLegalName.value;
        contact.id = this._contact.id === null || this._contact.id.match(/^\s*$/) ? genId() : this._contact.id;
        contact.internalContact = this._contact.internalContact;
        contact.isUser = controls.isUser.value || false;
        contact.isEntityOrOrganization = controls.isEntityOrOrganization.value || false;
        contact.entityOrOrganizationName = controls.entityOrOrganizationName.value;
        contact.lastName = controls.lastName.value;
        contact.displayName = contact.isEntityOrOrganization ? contact.entityOrOrganizationName : `${contact.firstName} ${contact.lastName}`;
        contact.middleInitial = controls.middleInitial.value;
        contact.organizationId = this._contact.organizationId;
        contact.primaryPhone = controls.primaryPhone.value;
        contact.secondaryPhone = controls.secondaryPhone.value;
        contact.suffix = controls.suffix.value;

        return Object.assign(this._contact, contact);
    }

    public onSaveDialogConfirm(): void {
        this.onSaveDialogClose();
        this.saveContact();
    }

    public onSaveDialogClose(): void {
        this.saveConfirmDialog.toggleDialog(false);
        this.dialogService.updateDialog(this.saveConfirmDialog);
    }

    public historyNotLoaded() {
        this.historyNotLoadedDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.historyNotLoadedDialog);
    }

    public history(): void {

        if (this.contact) {
            this.sidePanelService.newSidePanel(this.contactHistorySidePanel);
            this.sidePanelService.toggleSidePanel(true);
            this.loader.toggleLoader(false);
        }
    }

    public isUserUpdated(isSelected: boolean): void {
        //If the contact isn't currently a user, and the Create User textbox has just been checked
        //make sure to set the disabled field to false
        if (!this.contact.canLogIn && isSelected)
            this.contactDetailsForm.controls["disableAccount"].setValue(false);
    }

    public contactIsOnlyEmergency() {
        return this.contact.agentType == AgentType.EmergencyPOC;
    }

    updateMembership(groups: Group[]): void {
        this.groupMembershipChanged = true;
        this.userGroups = groups;
    }

    firstNameFieldValidator(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } | null => {
            if (!this.isEntityOrOrganization && control.value.length == 0)
                return { "required": true };

            return null;
        }
    }

    lastNameFieldValidator(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } | null => {
            if (!this.isEntityOrOrganization && control.value.length == 0)
                return { "required": true };

            return null;
        }
    }

    entityOrOrganizationNameFieldValidator(): ValidatorFn {
        return (control: AbstractControl): { [key: string]: any } | null => {
            if (this.isEntityOrOrganization && control && control.value && control.value.length == 0)
                return { "required": true };

            return null;
        }
    }

    toggleIsEntityOrOrganization() {
        this.isEntityOrOrganization = !this.isEntityOrOrganization;
        this.contactDetailsForm.get("lastName").updateValueAndValidity();
        this.contactDetailsForm.get("firstName").updateValueAndValidity();
        this.contactDetailsForm.get("entityOrOrganizationName").updateValueAndValidity();
        this.contactDetailsForm.updateValueAndValidity();
    }
}
